import React, { FC, useMemo } from 'react';
import { BasicContainer, BodyText, BodyTextSmall, FlexDynamic, FlexRow, LinkAsButton } from '@minecraft.atoms';
import { useTranslation } from '@blocs.i18n';
import { dateFormat, getDate } from '@minecraft.date-utils';
import { gqlSubscription } from '@minecraft.graphql-types';
import { ArtistSubscriptionFragment, TrialType } from '@minecraft.graphql-operations';
import { getTrialTimeLeft } from '../helpers/trialDuration';
import { useRetention } from '../useRetention';
import { useBillingPopup } from '../BillingPopupContext';
import { PaywallLocation } from '../enums/PaywallLocation';

interface TrialDaysCountdownProps {
  subscription?: gqlSubscription | ArtistSubscriptionFragment;
  artistId?: string | number;
}

export const TrialDaysCountdown: FC<TrialDaysCountdownProps> = ({ subscription, artistId }) => {
  const { t } = useTranslation();
  const { redirectToRetentionUrl, loading: retentionUrlLoading } = useRetention();
  const billingPopup = useBillingPopup();

  const trialTimeLeft = useMemo(() => {
    if (subscription?.trialEndsAt) {
      return getTrialTimeLeft(subscription.trialEndsAt as string);
    }
    return null;
  }, [subscription?.trialEndsAt]);

  if (!subscription || !artistId || !trialTimeLeft) {
    return null;
  }

  const isCardlessTrial = subscription?.trialType === TrialType.Cardless;
  const { hasExpired, isInvalidDate, days, hours, minutes } = trialTimeLeft;
  const isDueTomorrow = days === 1;
  const isDueNow = days === 0 && hours === 0 && minutes === 0;
  const trialLengthDays = subscription?.trialLengthDays ?? 30;

  if (isInvalidDate || hasExpired || isDueNow) {
    return null;
  }

  return (
    <BasicContainer w="full" bgColor="warning" data-testid="paywall-trial-plan-countdown">
      <FlexRow className="cn_atom_py-2 cn_atom_pl-4 cn_atom_pr-0 cn_atom_md:px-6" alignItems="center">
        <FlexDynamic
          w="fit"
          justifyContent="left"
          direction="column"
          mdProps={{ alignContent: 'center', alignItems: 'center', justifyItems: 'center' }}
        >
          {days === 0 ? (
            <BodyTextSmall color="disabled">
              {t('common:paywall.trial.countdown.endingTodayTitle', {
                trialLengthDays,
                trialHoursLeft: hours,
                trialMinutesLeft: minutes,
              })}
            </BodyTextSmall>
          ) : (
            <BodyText fontWeight="semibold">
              {t(
                isDueTomorrow
                  ? 'common:paywall.trial.countdown.oneDayLeftTitle'
                  : 'common:paywall.trial.countdown.title',
                { trialLengthDays, trialDaysLeft: days }
              )}
            </BodyText>
          )}
          <BodyTextSmall color="disabled">
            {t('common:paywall.trial.countdown.endsAt', {
              trialEndsAt: getDate(subscription.trialEndsAt, dateFormat.MONTH_DAY_YEAR),
            })}
          </BodyTextSmall>
        </FlexDynamic>
        {!isCardlessTrial && (
          <LinkAsButton
            disabled={retentionUrlLoading}
            className="cn_atom_pr-4"
            label={t('common:paywall.trial.countdown.cancel')}
            onClick={async (e) => {
              e.preventDefault();
              await redirectToRetentionUrl();
            }}
          />
        )}
        {isCardlessTrial && (
          <LinkAsButton
            className="cn_atom_pr-4"
            label={t('common:paywall.plan.upgrade')}
            onClick={(e) => {
              e.preventDefault();
              billingPopup.show({ location: PaywallLocation.ACTIVE_CARDLESS_TRIAL_MODAL });
            }}
          />
        )}
      </FlexRow>
    </BasicContainer>
  );
};
